import { FlexColumnSpaced, FlexRowSpacedRightAligned } from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { Button } from '../button/index';
import { Dialog } from '../mui-dialog/index';
import { useClickToDismissContext } from '../popover/hooks';
export * from './hooks';

function PromptModal({ message, shouldBlock, onConfirm }: { message: string; shouldBlock: boolean | (() => boolean); onConfirm?: () => void }) {
	const blocker = useBlocker(shouldBlock ?? !!message);
	return (
		<>
			{blocker.state === 'blocked' ? (
				<Dialog open={true}>
					<Padded>
						<FlexColumnSpaced>
							{message}
							<FlexRowSpacedRightAligned>
								<Button
									variant="secondary"
									onClick={() => {
										blocker.reset();
									}}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									onClick={() => {
										onConfirm?.();
										blocker.proceed();
									}}
								>
									Continue
								</Button>
							</FlexRowSpacedRightAligned>
						</FlexColumnSpaced>
					</Padded>
				</Dialog>
			) : null}
		</>
	);
}

/** The preferred component to mount to handle dirty state concerns - simply flag dirty with a message and it will prevent soft (router) and hard (F5) navigation */
export const DirtyState: React.FC<{ dirty: boolean | (() => boolean); message: string; onConfirm?: () => void }> = ({
	dirty,
	message,
	onConfirm
}) => {
	const ctd = useClickToDismissContext();
	useEffect(() => {
		const handleBeforeunload = (event: BeforeUnloadEvent) => {
			const currentDirty = typeof dirty === 'function' ? dirty() : dirty;
			if (!currentDirty) return '';

			event.preventDefault();

			return (event.returnValue = message);
		};

		if (dirty) {
			ctd.childPortalOpened(`dirtyState_${message}`, () => {
				const currentDirty = typeof dirty === 'function' ? dirty() : dirty;
				if (!currentDirty) return true;

				return confirm(message);
			});
		}

		window.addEventListener('beforeunload', handleBeforeunload);

		return () => {
			if (dirty) ctd.childPortalClosed(`dirtyState_${message}`);
			window.removeEventListener('beforeunload', handleBeforeunload);
		};
	}, [dirty, message]);

	if (!dirty) return null;

	return (
		<PromptModal
			message={message}
			shouldBlock={() => {
				const isDirty = typeof dirty === 'function' ? dirty() : dirty;
				return isDirty;
			}}
			onConfirm={onConfirm}
		/>
	);
};
